import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { entero } from '../lib/formato.js';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Cargando, Error as AvisoError, Vacio } from '../piezas/Estados.jsx';
import { useMask, useSesion } from '../piezas/Sesion.jsx';

interface Fila {
  almacen_clave: string;
  almacen: string;
  almacen_tipo: string;
  numero_parte: string;
  articulo: string;
  unidad: string;
  controla_serie: boolean;
  cantidad: number;
  valor_centavos?: number;
  moneda?: string;
}

export function Existencias(): JSX.Element {
  const [filas, setFilas] = useState<Fila[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [conSaldo, setConSaldo] = useState(true);
  const { veMontos } = useSesion();
  const mask = useMask();

  useEffect(() => {
    setFilas(null);
    api
      .obtener<Fila[]>(`/v1/existencias${conSaldo ? '?con_saldo=1' : ''}`)
      .then(({ datos }) => setFilas(datos))
      .catch(setError);
  }, [conSaldo]);

  return (
    <>
      <Pagehead />

      <div className="fila wrap" style={{ marginBottom: 12 }}>
        <div className="chips">
          <button
            type="button"
            className={conSaldo ? 'chip on' : 'chip'}
            onClick={() => setConSaldo(true)}
          >
            Con saldo
          </button>
          <button
            type="button"
            className={!conSaldo ? 'chip on' : 'chip'}
            onClick={() => setConSaldo(false)}
          >
            Todo el catalogo
          </button>
        </div>
      </div>

      {error !== null && <AvisoError error={error} />}

      <div className="card p0">
        {filas === null ? (
          <Cargando que="las existencias" />
        ) : filas.length === 0 ? (
          <Vacio
            mensaje={conSaldo ? 'Ningun almacen tiene saldo' : 'No hay articulos dados de alta'}
            icono="📦"
          />
        ) : (
          <table data-prueba="tabla-existencias">
            <thead>
              <tr>
                <th>Almacen</th>
                <th>Numero de parte</th>
                <th>Articulo</th>
                <th className="num">Cantidad</th>
                {/* La columna de valor no se dibuja para quien no ve montos.
                    Una columna de puros guiones solo informa que hay algo
                    que no se puede ver. */}
                {veMontos && <th className="num">Valor</th>}
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={`${f.almacen_clave}-${f.numero_parte}`}>
                  <td>
                    {f.almacen}
                    <div className="muted" style={{ fontSize: 11 }}>
                      {f.almacen_tipo === 'ALM_RESGUARDO' ? 'Resguardo de IDC' : 'CEDIS'}
                    </div>
                  </td>
                  <td className="folio">{f.numero_parte}</td>
                  <td>
                    {f.articulo}
                    {f.controla_serie && (
                      <span className="tag t-cold" style={{ marginLeft: 6 }}>
                        serializado
                      </span>
                    )}
                  </td>
                  <td className="num">
                    {entero(f.cantidad)} <span className="muted">{f.unidad}</span>
                  </td>
                  {veMontos && <td className="num">{mask(f.valor_centavos, f.moneda)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

interface Resguardo {
  idc_clave: string;
  idc: string;
  estatus: string;
  piezas: number;
  valor_centavos?: number;
  moneda?: string;
}

export function Resguardos(): JSX.Element {
  const [filas, setFilas] = useState<Resguardo[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const { veMontos } = useSesion();
  const mask = useMask();

  useEffect(() => {
    api
      .obtener<Resguardo[]>('/v1/resguardos')
      .then(({ datos }) => setFilas(datos))
      .catch(setError);
  }, []);

  return (
    <>
      <Pagehead />
      {error !== null && <AvisoError error={error} />}

      <div className="card p0">
        {filas === null ? (
          <Cargando que="los resguardos" />
        ) : filas.length === 0 ? (
          <Vacio mensaje="Ningun IDC tiene resguardo abierto" icono="🎒" />
        ) : (
          <table data-prueba="tabla-resguardos">
            <thead>
              <tr>
                <th>IDC</th>
                <th>Estatus</th>
                <th className="num">Piezas</th>
                {veMontos && <th className="num">Valor</th>}
              </tr>
            </thead>
            <tbody>
              {filas.map((r) => (
                <tr key={r.idc_clave}>
                  <td>{r.idc}</td>
                  <td>
                    <span className={r.estatus === 'IDC_ACTIVO' ? 'tag t-ok' : 'tag t-mute'}>
                      {r.estatus === 'IDC_ACTIVO' ? 'Activo' : 'No activo'}
                    </span>
                  </td>
                  {/* Quien se quedo sin equipo es justamente el que hay que
                      dotar, asi que el cero se marca en vez de pasar
                      desapercibido. */}
                  <td className="num">
                    {r.piezas === 0 ? (
                      <span className="tag t-warm">sin equipo</span>
                    ) : (
                      entero(r.piezas)
                    )}
                  </td>
                  {veMontos && <td className="num">{mask(r.valor_centavos, r.moneda)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
